import React, {useContext, useMemo} from "react"
import {Box, TableCell, TableRow, useTheme} from "@mui/material"
import {ScrollableTable, TableBody, TableHead} from "./ScrollableTable"
import {DataContext} from "../../../../src/context/DataContextProvider"
import {getMerkmal} from "../../../misc/widget-functions"
import {parseStringToNumber} from "../../../misc/utils"

const fmtPercent = (n, isLoading, fractionDigits) => {
    const parsedFractionDigits = parseStringToNumber(fractionDigits)
    if (isLoading) return "0 %"
    if (n == null) return ""
    return (
        new Intl.NumberFormat("de-DE", {
            minimumFractionDigits: parsedFractionDigits,
            maximumFractionDigits: parsedFractionDigits,
        }).format(n) + " %"
    )
}

export const fmtAlways = (n, isLoading, fractionDigits) => {
    const parsedFractionDigits = parseStringToNumber(fractionDigits)
    if (isLoading) return "0 €"
    if (n == null) return ""
    return (
        new Intl.NumberFormat("de-DE", {
            minimumFractionDigits: parsedFractionDigits,
            maximumFractionDigits: parsedFractionDigits,
        }).format(n) + " €"
    )
}

export const avRiesterTablePrototype = [
    {
        "key": "hasPartner",
        "type": "YESNO",
        "label": "Partner Spalten anzeigen",
        "size": 3,
    },
    {
        "key": "isLoading",
        "type": "YESNO",
        "label": "Im Ladezustand",
        "size": 3,
    },
    {
        "key": "fractionDigits",
        "type": "INTEGER",
        "label": "Anzahl Nachkommastellen",
        "size": 3,
        "defaultValue": 0
    },
]

const loadingColor = "#7c8a98"

const showRow = (value, displayRule) => displayRule ? displayRule(value) : true

const AvRiesterTable = ({widget}) => {
    const {hasPartner = false, isLoading = false} = widget
    const merkmal = getMerkmal(widget)
    const {getDataFromPointer} = useContext(DataContext)
    const {kunde, ehepartner, gesamt} = getDataFromPointer(merkmal?.pointer) ?? {}
    const {
        primary: steuerersparnis,
        secondary: kinderzulage,
        tertiary: grundzulage,
        quartary: nettobeitrag,
        quintary: startbonus,
    } = useTheme()?.AvRiester ?? {}

    const tableRows = useMemo(() => [
        {key: "gesamtbeitrag", label: "Gesamtbeitrag", bold: true},
        {key: "kinderzulage", label: "Kinderzulage", color: kinderzulage},
        {key: "grundzulage", label: "Grundzulage", color: grundzulage},
        {key: "startbonus", label: "Berufseinsteigerbonus", displayRule: (value) => value > 0, color: startbonus},
        {key: "eigenbeitrag", label: "Eigenbeitrag"},
        {key: "steuerersparnis", label: "Steuerersparnis", color: steuerersparnis, gesamtOnly: true},
        {key: "nettobeitrag", label: "Nettobeitrag", color: nettobeitrag, gesamtOnly: true},
        {key: "foerderquote", label: "Förderquote", gesamtOnly: true, large: true, format: fmtPercent},
    ], [kinderzulage, grundzulage, startbonus, steuerersparnis, nettobeitrag])

    const loadingSx = useMemo(
        () => isLoading ? {color: `${loadingColor} !important`} : {},
        [isLoading],
    )

    return (
        <ScrollableTable noWrap>
            <TableHead>
                <TableRow>
                    <TableCell></TableCell>
                    {hasPartner && <TableCell align="right">Kunde</TableCell>}
                    {hasPartner && <TableCell align="right">Partner</TableCell>}
                    <TableCell align="right">Gesamt</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {tableRows.map(({key, label, color, gesamtOnly, large, bold, format: rowFmt, displayRule}) => (
                    showRow(gesamt?.[key], displayRule) && <TableRow
                        key={key}
                        sx={{
                            ...(large && {"& .MuiTableCell-root": {fontSize: "16px", fontWeight: 700}}),
                            ...(bold && !large && {"& .MuiTableCell-root": {fontWeight: 700}}),
                        }}
                    >
                        <TableCell sx={loadingSx}>
                            <Box sx={{display: "flex", alignItems: "center", gap: "8px"}}>
                                {color && (
                                    <Box
                                        sx={{
                                            width: 16,
                                            height: 16,
                                            borderRadius: "50%",
                                            bgcolor: isLoading ? loadingColor : color,
                                            flexShrink: 0,
                                        }}
                                    />
                                )}
                                {label}
                            </Box>
                        </TableCell>
                        {hasPartner && (
                            <TableCell align="right" sx={loadingSx}>
                                {gesamtOnly ? "" : (rowFmt ?? fmtAlways)(kunde?.[key], isLoading, widget?.fractionDigits)}
                            </TableCell>
                        )}
                        {hasPartner && (
                            <TableCell align="right" sx={loadingSx}>
                                {gesamtOnly ? "" : (rowFmt ?? fmtAlways)(ehepartner?.[key], isLoading, widget?.fractionDigits)}
                            </TableCell>
                        )}
                        <TableCell align="right" sx={loadingSx}>
                            {(rowFmt ?? fmtAlways)(gesamt?.[key], isLoading, widget?.fractionDigits)}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </ScrollableTable>
    )
}

export default AvRiesterTable
